import { usePersonReadDataContext } from '@aibees/elephant/context';
import {
  useWorkHistoryCurrentRoles,
  useWorkHistoryFullHistory,
} from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/components/work-history';
import {
  getJobChangeAlertMessage,
  isExperienceChangeHappened,
} from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/utils';
import { getRoleDisplayLabel, RoleDisplayLabel } from '@aibees/elephant/utils';
import { classnames } from '@aibees/shared/utils';
import { Alert } from '@mui/material';
import { memo, useMemo } from 'react';
import * as SX from './sx';
import type { JobChangeAlertMessageProps as Props } from './types';

export const JobChangeAlertMessage = memo(({ className }: Props) => {
  const { api } = usePersonReadDataContext();
  const allCurrentRoles = useWorkHistoryCurrentRoles(api?.data);
  const fullWorkHistory = useWorkHistoryFullHistory(api?.data);

  const currentLabels = useMemo<RoleDisplayLabel[]>(
    () =>
      allCurrentRoles
        .map((group) => getRoleDisplayLabel(group[0]))
        .filter((l): l is RoleDisplayLabel => l !== null),
    [allCurrentRoles]
  );

  const previousLabel = useMemo(
    () =>
      allCurrentRoles.length === 0
        ? getRoleDisplayLabel(fullWorkHistory[0])
        : null,
    [allCurrentRoles.length, fullWorkHistory]
  );

  // Fires on any work-history movement, not only a company change — an
  // experience-only change is a genuine Profile Insights update and the alert
  // should keep showing for it.
  const hasExperienceChange = isExperienceChangeHappened(api?.data);

  if (!hasExperienceChange) return null;

  const message = getJobChangeAlertMessage({
    firstName: api?.data?.profile?.first_name,
    currentLabels,
    previousLabel,
  });

  return (
    <Alert
      className={classnames('JobChangeAlertMessage', className)}
      variant="standard"
      severity="info"
      sx={SX.root}
      icon={false}
    >
      {message}
    </Alert>
  );
});

JobChangeAlertMessage.displayName = 'JobChangeAlertMessage';
